import React from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import {
  Assignment,
  Biotech,
  CategoryOutlined,
  FlagOutlined,
  InsertLinkOutlined,
  LabelOutlined,
  Link as LinkIcon,
  Numbers,
  PlaceOutlined,
  ScheduleOutlined,
  Science,
  ScienceOutlined,
  SettingsOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
  WebOutlined,
} from "@mui/icons-material";

const ViewEnviroTestKit = ({ kit }) => {
  if (!kit) {
    return null;
  }

  // otherTestsToAddJson / sampleSlateJson are stored as comma separated codes.
  const formatCodes = (value) =>
    (value || "")
      .split(",")
      .map((code) => code.trim())
      .filter(Boolean)
      .join(", ");

  const InfoRow = ({ icon: Icon, label, value }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
      <Icon sx={{ color: "#1976d2", fontSize: 18, mr: 1, mt: "2px" }} />
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, minWidth: "180px", color: "#555" }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ ml: 1, color: "#333", wordBreak: "break-word" }}
      >
        {value || value === 0 ? value : "N/A"}
      </Typography>
    </Box>
  );

  const SectionCard = ({ icon: Icon, title, children }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Icon sx={{ color: "#1976d2", mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {children}
      </CardContent>
    </Card>
  );

  const RegistrationLink = ({ url }) =>
    url ? (
      <Link href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </Link>
    ) : (
      "N/A"
    );

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1976d2" }}>
          {kit.vlpName || "Enviro Test Kit"}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "#666" }}>
          {kit.nameFromSite || "N/A"}
          {kit.sampleKitId ? ` — Kit #${kit.sampleKitId}` : ""}
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Kit Identification */}
        <Grid item xs={12} md={6}>
          <SectionCard icon={Assignment} title="Kit Identification">
            <InfoRow icon={Numbers} label="Kit Id" value={kit.sampleKitId} />
            <InfoRow icon={WebOutlined} label="Kit Type (internal use)" value={kit.vlpName} />
            <InfoRow
              icon={CategoryOutlined}
              label="TypeForm Name"
              value={kit.nameFromSite}
            />
            <InfoRow
              icon={LabelOutlined}
              label="Short Label Name"
              value={kit.shortLabelName}
            />
          </SectionCard>
        </Grid>

        {/* Sampling & Handling */}
        <Grid item xs={12} md={6}>
          <SectionCard icon={Science} title="Sampling & Handling">
            <InfoRow
              icon={ScienceOutlined}
              label="Number of Samples"
              value={kit.numberOfSamples}
            />
            <InfoRow
              icon={PlaceOutlined}
              label="Sample Location"
              value={kit.sampleLocation}
            />
            <InfoRow icon={FlagOutlined} label="Priority" value={kit.priority} />
            <InfoRow
              icon={ScheduleOutlined}
              label="Turn Around Time"
              value={kit.turnAroundTime ? `${kit.turnAroundTime} days` : ""}
            />
          </SectionCard>
        </Grid>

        {/* Tests */}
        <Grid item xs={12} md={6}>
          <SectionCard icon={Biotech} title="Tests">
            <InfoRow
              icon={ScienceOutlined}
              label="Sample Slate"
              value={formatCodes(kit.sampleSlateJson)}
            />
            <InfoRow
              icon={Biotech}
              label="Other Tests To Add"
              value={formatCodes(kit.otherTestsToAddJson)}
            />
          </SectionCard>
        </Grid>

        {/* Availability */}
        <Grid item xs={12} md={6}>
          <SectionCard icon={SettingsOutlined} title="Availability">
            <InfoRow
              icon={
                kit.showInOrderGenerator !== false
                  ? VisibilityOutlined
                  : VisibilityOffOutlined
              }
              label="Show in Order Generator"
              value={kit.showInOrderGenerator !== false ? "Yes" : "No"}
            />
          </SectionCard>
        </Grid>

        {/* Registration Links */}
        <Grid item xs={12}>
          <SectionCard icon={InsertLinkOutlined} title="Registration Links">
            <InfoRow
              icon={LinkIcon}
              label="TypeForm Pre Pay Link"
              value={<RegistrationLink url={kit.typeFormLink} />}
            />
            <InfoRow
              icon={LinkIcon}
              label="TypeForm Post Pay Link"
              value={<RegistrationLink url={kit.typeFormLinkPostPay} />}
            />
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewEnviroTestKit;
